import { Alert, Box, Button, Grid2 as Grid, Link, Stack, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { useCommonApi } from '../api';
import { onSuccessCustomApiResponse } from '../api/utils';
import { contactDataSubjects } from '../data/contactData';
import { PageLayout, PageLayoutProps } from './common';
import { SelectGroupFormField } from './dynamicForm';
import { useAuth } from './hooks';
import { User } from './types';

interface ContactPageContentProps extends PageLayoutProps {}

export interface ContactFormType {
  subject: string;
  accidentNumber: string;
  phone: string;
  email: string;
  message: string;
}

interface ContactPageFormProps {
  user: User | null;
}

const ContactPageForm = ({ user }: ContactPageFormProps) => {
  const {
    mutate: sendContactForm,
    data: contactFormResponseData,
    isPending: isSending
  } = useMutation(useCommonApi().sendEmailForm());

  const methods = useForm<ContactFormType>({
    defaultValues: {
      subject: 'nehoda',
      accidentNumber: '',
      email: user?.email || '',
      phone: '',
      message: ''
    }
  });

  const {
    handleSubmit,
    formState: { errors },
    reset,
    control
  } = methods;

  const sendForm = (data: any) => {
    sendContactForm(
      {
        formData: data,
        key: 'contact-form'
      },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: () => reset()
        })
      }
    );
  };

  return (
    <>
      {contactFormResponseData &&
        (contactFormResponseData?.success ? (
          <Alert severity="success" sx={{ mb: 3 }}>
            Zpráva byla úspěšně odeslána. Budeme vás kontaktovat.
          </Alert>
        ) : (
          <Alert severity="error" sx={{ mb: 3 }}>
            Chyba u odesílání zprávy. Kontaktujte nás telefonicky nebo e-mailem.
          </Alert>
        ))}
      <form onSubmit={handleSubmit(sendForm)} noValidate>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Stack gap={2}>
              <Controller
                control={control}
                name="subject"
                render={({ field: { onChange, value } }) => (
                  <SelectGroupFormField
                    formField={{
                      name: 'subject',
                      id: 'subject',
                      label: 'Předmět dotazu',
                      options: contactDataSubjects
                    }}
                    value={value || ''}
                    onChange={onChange}
                    error={!!errors?.subject}
                  />
                )}
              />
              <Controller
                control={control}
                name="accidentNumber"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    label="Číslo nehody"
                    color="secondary"
                    fullWidth
                    error={Boolean(errors.accidentNumber)}
                    helperText={errors.accidentNumber?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
              <Controller
                control={control}
                name="phone"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    label="Telefonní číslo"
                    color="secondary"
                    fullWidth
                    error={Boolean(errors.phone)}
                    helperText={errors.phone?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
              <Controller
                control={control}
                name="email"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    label="E-Mail"
                    color="secondary"
                    disabled
                    fullWidth
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }} mt={3}>
            <Controller
              control={control}
              name="message"
              render={({ field: { onChange, value } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  label="Váš dotaz"
                  color="secondary"
                  placeholder="Zde napište dotaz na nás"
                  multiline
                  minRows={7}
                  fullWidth
                  error={Boolean(errors.message)}
                  helperText={errors.message?.message}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <Button type="submit" variant="contained" size="large" loading={isSending}>
              Odeslat
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export const ContactPage = (props: ContactPageContentProps) => {
  const { user } = useAuth();

  return (
    <PageLayout title="Napište nám">
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 9 }}>
          <Box padding={4} sx={{ backgroundColor: 'background.paper' }}>
            <ContactPageForm user={user} />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, lg: 3 }}>
          <Stack gap={2} sx={{ backgroundColor: 'background.paper' }} padding={4}>
            <Typography fontWeight="bold">Preferujete zavolání?</Typography>
            <Typography>Můžete nám zavolat na níže uvedené číslo v pracovní době</Typography>
            <Typography>
              Pondělí - Pátek <br />
              8:00 - 16:00
            </Typography>
            <Typography fontWeight="bold">
              <Link href="tel:+420778558664">+420 778 558 664</Link>
            </Typography>
          </Stack>
        </Grid>
      </Grid>
    </PageLayout>
  );
};
